import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import w from './SafetyEnv.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  환경 — GreenLink 전송  (/environment/greenlink)
//  [2026-10-08] 기획서 v7 4-5. 7영역: ①전송구조 ②데이터처리 ③통신방식 ④정상확인 ⑤장애유형 ⑥유지관리 ⑦문의.
//  [2026-10-22 사용자] ★본문 화이트화 + 다이어그램 '페이지당 1개'.
//    → 유일 다이어그램 = ① 전송구조(계측기→GreenLink) 셰브론. ⑥유지관리(구 flow)는 단순 카드로 격하(남발 금지).
//    히어로=다크 유지. ⑥ 장애처리 근거=브로셔 pg6([[evs-brochure-251128-data]]).
// ============================================================================

const HERO_IMG = '/images/hero/hero-guide.jpg';
const SLOGAN = '계측기·게이트웨이가 측정한 데이터를 수집·저장하고, VPN을 통해 환경부 GreenLink로 안정적으로 전송하며 전송 장애까지 관리하는 환경 데이터 전송 체계입니다.';

const Ic = ({ n }: { n: string }) => <div className={w.iconWrap}>{GUIDE_ICON[n]}</div>;
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
function Soon({ label }: { label: string }) {
  return (
    <div className={w.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

export default function EnvironmentGreenlink() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);
  return (
    <main className={w.page}>
      <PageLoader />
      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '환경', 'GreenLink 전송']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>GreenLink 전송</h1>
          <p className={w.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 전송 구조 — ★유일 다이어그램: 셰브론(계측기→GreenLink) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>STRUCTURE</p>
            <h2 className={w.h2}>전송 구조</h2>
            <p className={w.lead}>현장 측정부터 환경부 전송까지 하나의 흐름으로 연결됩니다.</p>
            <div className={w.chevron}>
              {[
                { n: '01', t: '계측기', i: 'gauge', d: '현장 측정', c: w.cBlue },
                { n: '02', t: '게이트웨이', i: 'gateway', d: '수집·표시·저장', c: w.cTeal },
                { n: '03', t: 'VPN', i: 'signal', d: '보안 전송', c: w.cIndigo },
                { n: '04', t: 'GreenLink', i: 'factory', d: '환경부 전송', c: w.cCoral },
              ].map((x) => (
                <div key={x.n} className={`${w.chevronStep} ${x.c}`}>
                  <div className={w.chevronIco}>{GUIDE_ICON[x.i]}</div>
                  <span className={w.chevronNum}>{x.n}</span>
                  <h3>{x.t}</h3><p>{x.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ② 데이터 처리 — 비비드 카드 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>DATA</p>
            <h2 className={w.h2}>데이터 처리</h2>
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="monitor" /><h3>수집·표시</h3><p>계측값을 수집해 7인치 LCD로 현장에서 확인합니다.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="database" /><h3>로컬 저장</h3><p>통신 장애 시에도 데이터를 보존합니다.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.solid}`}><Ic n="signal" /><h3>전송 주기</h3><p>측정값을 주기적으로 GreenLink에 전송합니다.</p></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ③ 통신 방식 — 칩 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>COMMUNICATION</p>
            <h2 className={w.h2}>통신 방식</h2>
            <p className={w.lead}>Ethernet·LTE·VPN 구성으로 현장 환경에 맞게 안정적으로 전송합니다.</p>
            <div className={w.chips}>
              {['Ethernet', 'LTE', 'VPN'].map((a) => <span key={a} className={w.chip}>{a}</span>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ④ 정상 확인 — 텍스트 + 준비중 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>STATUS</p>
            <h2 className={w.h2}>정상 확인</h2>
            <p className={w.lead}>현장 측정값·게이트웨이 상태·GreenLink 수신 상태를 확인해 정상 전송 여부를 점검합니다.</p>
            <Soon label="상태 화면 예시" />
          </Reveal>
        </div>
      </section>

      {/* ⑤ 장애 유형 — 비비드 카드 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>TROUBLESHOOTING</p>
            <h2 className={w.h2}>장애 유형</h2>
            <p className={w.lead}>전원·센서·배선·라우터·통신·서버별 증상과 대처를 안내합니다.</p>
            <div className={`${w.grid} ${w.grid4}`}>
              <div className={`${w.card} ${w.cAmber} ${w.solid}`}><Ic n="energy" /><h3>전원·배선</h3><p>전원 단절·배선 문제로 인한 미전송.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="gauge" /><h3>센서</h3><p>센서 오작동·오염으로 인한 이상값.</p></div>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="signal" /><h3>라우터·통신</h3><p>인터넷·LTE 불량으로 인한 미전송.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.solid}`}><Ic n="database" /><h3>서버</h3><p>GreenLink 수신 서버 측 문제.</p></div>
            </div>
            <a className={w.moreLink} onClick={() => nav('/support/faq')}>FAQ 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑥ 유지관리 — 비비드 카드(구 flow→단순 카드, 다이어그램 1개 원칙) */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>MAINTENANCE</p>
            <h2 className={w.h2}>유지관리</h2>
            <p className={w.lead}>원격 확인 → 방문 점검 → A/S 흐름으로 전송 장애를 신속히 조치합니다. 실제 A/S 조치 사례도 확인하실 수 있습니다.</p>
            <div className={`${w.grid} ${w.grid4}`}>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="bell" /><h3>비정상 인지</h3><p>카카오 알림으로 비정상 데이터를 즉시 인지합니다.</p></div>
              <div className={`${w.card} ${w.cAmber} ${w.solid}`}><Ic n="phone" /><h3>장애 접수</h3><p>IoT GW 고객센터로 장애를 접수합니다.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.solid}`}><Ic n="ai" /><h3>원인 분석</h3><p>기술팀이 데이터를 분석해 원인을 파악합니다. (자체개선계획서 안내)</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="certified" /><h3>처리 완료</h3><p>조치 완료 후 자체개선완료보고서까지 안내합니다.</p></div>
            </div>
            <p className={w.note} style={{ marginTop: 18, fontSize: 13, color: '#8a94a3' }}>※ 축적된 현장 지식·노하우로 원인을 빠르게 파악하며, 접수 후 신속 처리를 원칙으로 합니다.</p>
            <a className={w.moreLink} onClick={() => nav('/cases?tab=environment')}>환경 A/S 사례 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑦ 문의 — CTA 틴트밴드 */}
      <section className={w.finalCta}>
        <div className={w.inner}>
          <Reveal>
            <h2 className={w.h2}>미수신·비정상 전송을 점검해 드립니다</h2>
            <p className={w.lead}>전송 장애가 의심되면 A/S를 접수해 주세요. 유지관리 상담도 가능합니다.</p>
            <div className={w.ctaRow}>
              <Button variant="pill" onClick={() => nav('/support/as')}>A/S 접수</Button>
              <a className={w.ctaGhost} onClick={() => nav('/support/consult')}>유지관리 상담</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
